import { useState } from 'react';
import { Badge, Button, Modal } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { NewStavbaModal } from '../../components/stavba';
import { useStavby, useDeleteStavba } from '../../api/hooks';
import type { Stavba } from '../../api/schemas';

/**
 * Přehled staveb — napojen na /api/stavby.
 * Odvozené sloupce (status, progress) nejsou v backend modelu; ukazujeme
 * jen to, co reálně přichází (nazev, zakazka, adresa, data).
 */

function deriveStatus(s: Stavba): { tone: 'success' | 'yellow' | 'neutral'; label: string } {
  const today = new Date();
  const end = s.datum_ukonceni ? parseCzechDate(s.datum_ukonceni) : null;
  const start = s.datum_zahajeni ? parseCzechDate(s.datum_zahajeni) : null;
  if (end && end < today) return { tone: 'neutral', label: 'Dokončena' };
  if (start && start > today) return { tone: 'yellow', label: 'Plánovaná' };
  return { tone: 'success', label: 'Aktivní' };
}

function parseCzechDate(str: string): Date | null {
  // Formát: "22. 04. 2026" nebo ISO
  const czech = str.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/);
  if (czech) {
    return new Date(Number(czech[3]), Number(czech[2]) - 1, Number(czech[1]));
  }
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function StavbyPage() {
  const { data: stavby, isLoading, error } = useStavby();
  const del = useDeleteStavba();
  const [newOpen, setNewOpen] = useState(false);
  const [editStavba, setEditStavba] = useState<Stavba | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Stavba | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleteError(null);
    try {
      await del.mutateAsync(confirmDelete.id);
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Nepodařilo se smazat stavbu.');
    }
  }

  const columns: ListColumn<Stavba>[] = [
    { key: 'zakazka_cislo', label: 'Č. zakázky', width: '110px', mono: true, dim: true },
    { key: 'nazev', label: 'Stavba', primary: true },
    { key: 'adresa', label: 'Adresa', dim: true },
    { key: 'datum_zahajeni', label: 'Zahájení', width: '104px', mono: true, dim: true },
    { key: 'datum_ukonceni', label: 'Dokončení', width: '104px', mono: true, dim: true },
    {
      key: 'status',
      label: 'Stav',
      width: '120px',
      render: (s) => {
        const st = deriveStatus(s);
        return (
          <Badge tone={st.tone} pill dot>
            {st.label}
          </Badge>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '88px',
      align: 'right',
      render: (s) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <Button
            size="sm"
            variant="ghost"
            aria-label="Upravit stavbu"
            title="Upravit"
            onClick={() => setEditStavba(s)}
          >
            ✎
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-label="Smazat stavbu"
            title="Smazat"
            onClick={() => {
              setDeleteError(null);
              setConfirmDelete(s);
            }}
          >
            🗑
          </Button>
        </div>
      ),
    },
  ];

  const rows = stavby ?? [];
  const activeCount = rows.filter((s) => deriveStatus(s).label === 'Aktivní').length;

  return (
    <>
    <ListPage
      breadcrumb={['Přehled', 'Moje stavby']}
      title="Moje stavby"
      subtitle={
        isLoading
          ? 'Načítám…'
          : error
            ? 'Chyba při načítání dat'
            : `${rows.length} zakázek · ${activeCount} aktivních`
      }
      status={isLoading ? 'neutral' : error ? 'draft' : 'done'}
      statusText={
        isLoading
          ? 'Načítám…'
          : error
            ? 'Chyba'
            : `${activeCount} aktivních`
      }
      actions={<Button variant="primary" onClick={() => setNewOpen(true)}>+ Nová stavba</Button>}
      searchPlaceholder="Hledat podle čísla, názvu nebo adresy…"
    >
      {isLoading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--sd-doc-dim)' }}>Načítám stavby…</div>
      ) : error ? (
        <div style={{
          padding: 24, borderRadius: 4,
          background: 'var(--sd-danger-soft)',
          border: '1px solid rgba(248, 113, 113, 0.4)',
          color: 'var(--sd-danger)', fontSize: 14,
        }}>
          Nepodařilo se načíst stavby: {error instanceof Error ? error.message : 'neznámá chyba'}
        </div>
      ) : (
        <ListTable
          columns={columns}
          rows={rows}
          rowKey={(s) => s.id}
          fluid
          emptyTitle="Zatím žádné stavby"
          emptyText="Vytvořte první stavbu tlačítkem „+ Nová stavba“."
        />
      )}
    </ListPage>
    <NewStavbaModal open={newOpen} onClose={() => setNewOpen(false)} />
    <NewStavbaModal
      open={editStavba !== null}
      stavba={editStavba}
      onClose={() => setEditStavba(null)}
    />
    <Modal
      open={confirmDelete !== null}
      onClose={() => setConfirmDelete(null)}
      title="Smazat stavbu"
      footer={
        <>
          <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
            Zrušit
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={del.isPending}>
            {del.isPending ? 'Mažu…' : 'Smazat'}
          </Button>
        </>
      }
    >
      <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--sd-doc-ink)' }}>
        Opravdu chcete smazat stavbu <strong>{confirmDelete?.nazev}</strong>? Spolu s ní se
        nenávratně odstraní všechny související záznamy (deník, vady, fotky, dokumenty…).
      </p>
      {deleteError && (
        <div style={{
          marginTop: 12, padding: '10px 12px', borderRadius: 4,
          background: 'var(--sd-danger-soft)',
          border: '1px solid rgba(248, 113, 113, 0.4)',
          color: 'var(--sd-danger)', fontSize: 13,
        }}>
          {deleteError}
        </div>
      )}
    </Modal>
    </>
  );
}
